import { defineStore } from 'pinia'
import type { ResolvedTheme } from '@/shared/theme/theme'

export type GlassThemeName = 'dark' | 'light'

const semanticAliases: Record<string, string> = {
  '--vf-body-bg': 'var(--bg-deep)', '--vf-page-bg': 'var(--bg-color)', '--vf-shell-bg': 'transparent',
  '--vf-page-layer': 'radial-gradient(circle at 16% 8%, var(--mesh-a), transparent 34%), radial-gradient(circle at 88% 26%, var(--mesh-b), transparent 31%), linear-gradient(180deg, transparent, var(--bg-color))',
  '--vf-shell-overlay': 'none', '--vf-shell-overlay-opacity': '0', '--vf-title-shadow': 'none',
  '--vf-text-primary': 'var(--text-primary)', '--vf-text-secondary': 'var(--text-secondary)', '--vf-text-muted': 'var(--text-secondary)',
  '--vf-page-title': 'var(--text-primary)', '--vf-card-title': 'var(--text-primary)', '--vf-text-cyan': 'var(--accent-primary)',
  '--vf-card-bg': 'var(--glass-bg)', '--vf-choice-card-bg': 'var(--glass-bg)', '--vf-service-card-bg': 'var(--glass-bg)',
  '--vf-secondary-bg': 'var(--glass-bg)', '--vf-control-bg': 'var(--glass-bg)', '--vf-card-border': 'var(--glass-border)',
  '--vf-shell-border': 'var(--glass-border)', '--vf-info-border': 'var(--glass-border)', '--vf-control-border': 'var(--glass-border)',
  '--vf-card-shadow': 'var(--glass-shadow-soft)', '--vf-shell-shadow': 'var(--glass-shadow)', '--vf-control-shadow': 'var(--glass-shadow-soft)',
  '--vf-primary-bg': 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))', '--vf-primary-text': 'var(--accent-on)',
  '--vf-primary-border': 'var(--glass-highlight)', '--vf-primary-shadow': 'var(--accent-shadow)',
  '--vf-secondary-border': 'var(--glass-border)', '--vf-text-action': 'var(--text-primary)', '--vf-text-action-bg': 'var(--glass-bg)',
  '--vf-text-action-border': 'var(--glass-border)', '--vf-text-action-shadow': 'var(--glass-shadow-soft)', '--vf-focus-ring': 'var(--accent-primary)',
  '--vf-topbar-bg': 'var(--glass-bg-strong)', '--vf-topbar-title': 'var(--text-primary)', '--vf-topbar-icon': 'var(--text-primary)',
  '--vf-topbar-action': 'var(--text-primary)', '--vf-topbar-border': 'var(--glass-border)', '--vf-topbar-shadow': 'var(--glass-shadow-soft)',
  '--vf-language-bg': 'var(--glass-bg)', '--vf-language-border': 'var(--glass-border)', '--vf-language-shadow': 'var(--glass-shadow-soft)',
  '--vf-language-track': 'var(--accent-surface)', '--vf-language-thumb-bg': 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
  '--vf-language-thumb-shadow': 'var(--glass-shadow-soft)', '--vf-language-active-text': 'var(--accent-on)', '--vf-language-inactive-text': 'var(--text-secondary)',
  '--vf-icon-bg': 'var(--accent-surface)', '--vf-icon-color': 'var(--accent-primary)',
  '--vf-icon-border': 'var(--accent-border)', '--vf-icon-shadow': 'var(--glass-shadow-soft)',
  '--vf-hero-bg': 'var(--glass-bg)', '--vf-hero-border': 'var(--glass-border)', '--vf-hero-shadow': 'var(--glass-shadow)',
  '--vf-media-frame-bg': 'var(--glass-bg)', '--vf-media-frame-border': 'var(--glass-border)', '--vf-media-frame-shadow': 'var(--glass-shadow)',
  '--vf-vr-card-bg': 'linear-gradient(135deg, var(--accent-surface), var(--glass-bg-strong))', '--vf-vr-card-border': 'var(--accent-border)',
  '--vf-entry-notice-bg': 'var(--glass-bg)', '--vf-entry-notice-text': 'var(--text-primary)', '--vf-entry-notice-heading': 'var(--text-primary)',
  '--vf-entry-notice-link': 'var(--accent-primary)', '--vf-entry-notice-quote-bg': 'var(--glass-bg-strong)', '--vf-entry-notice-quote-border': 'var(--accent-primary)',
  '--vf-summary-card-bg': 'var(--glass-bg-strong)', '--vf-summary-title': 'var(--text-primary)', '--vf-summary-muted': 'var(--text-secondary)',
  '--vf-summary-inverse': 'var(--text-primary)', '--vf-summary-kicker': 'var(--accent-primary)', '--vf-summary-snapshot-label': 'var(--text-secondary)',
  '--vf-summary-map-border': 'var(--glass-border)', '--vf-summary-map-shadow': 'var(--glass-shadow-soft)',
  '--vf-summary-action-bg': 'var(--glass-bg)', '--vf-summary-action-border': 'var(--glass-border)',
  '--vf-summary-action-shadow': 'var(--glass-shadow-soft)', '--vf-broadcast-sheet-bg': 'var(--glass-bg-strong)', '--vf-broadcast-text': 'var(--text-primary)',
  '--vf-broadcast-muted': 'var(--text-secondary)', '--vf-broadcast-item-bg': 'var(--glass-bg)', '--vf-broadcast-close-bg': 'var(--glass-bg)',
  '--vf-broadcast-close-border': 'var(--glass-border)', '--vf-broadcast-detail-overlay': 'var(--scrim)', '--vf-annotation-overlay': 'var(--scrim)',
  '--vf-annotation-panel-bg': 'var(--glass-bg-strong)', '--vf-annotation-panel-border': 'var(--glass-border)', '--vf-annotation-text': 'var(--text-primary)',
  '--vf-image-preview-bg': 'var(--scrim)',
}

export const useThemeStore = defineStore('theme', {
  state: () => ({ name: 'dark' as GlassThemeName, initialized: false, skinVariables: {} as Record<string, string> }),
  actions: {
    initialize() {
      if (this.initialized || typeof document === 'undefined') return
      try { localStorage.removeItem('venue-audience-glass-theme-v1') } catch { /* Retire the former standalone day/night preference. */ }
      this.initialized = true
      this.apply()
    },
    adoptResolvedTheme(theme: ResolvedTheme) {
      this.initialize()
      this.name = isDarkColor(theme.canvas.page) ? 'dark' : 'light'
      const usesLayeredGlass = this.name === 'dark' && theme.elevationPreset === 'layered'
      this.skinVariables = {
        '--bg-color': theme.canvas.page,
        '--bg-deep': theme.canvas.page,
        '--text-primary': theme.canvas.foreground,
        '--text-secondary': theme.canvas.secondary,
        '--glass-bg': usesLayeredGlass
          ? `linear-gradient(112deg, ${transparentColor(theme.canvas.accent, .46)} 0%, ${transparentColor(theme.canvas.surface, .82)} 36%, ${transparentColor(theme.canvas.page, .92)} 100%)`
          : transparentColor(theme.canvas.surface, .72),
        '--glass-bg-strong': usesLayeredGlass
          ? `linear-gradient(145deg, ${transparentColor(theme.canvas.elevated, .9)}, ${transparentColor(theme.canvas.page, .96)} 74%)`
          : transparentColor(theme.canvas.elevated, .9),
        '--vf-topbar-opaque-bg': theme.canvas.page,
        '--glass-border': transparentColor(theme.canvas.border, .72),
        '--glass-highlight': transparentColor(theme.canvas.inverse, .24),
        '--accent-primary': theme.canvas.accent,
        '--accent-secondary': theme.canvas.routeEntry,
        '--accent-on': contrastText(theme.canvas.accent),
        '--accent-surface': transparentColor(theme.canvas.accent, .14),
        '--accent-border': transparentColor(theme.canvas.accent, .42),
        '--accent-shadow': `0 12px 28px ${transparentColor(theme.canvas.accent, usesLayeredGlass ? .38 : .28)}`,
        '--mesh-a': transparentColor(theme.canvas.accent, usesLayeredGlass ? .12 : .28),
        '--mesh-b': transparentColor(theme.canvas.routeEntry, usesLayeredGlass ? .14 : .2),
        '--mesh-c': transparentColor(theme.canvas.routeExit, .18),
        '--scrim': theme.canvas.scrim,
        '--stadium-ring': transparentColor(theme.canvas.accent, .44),
        '--stadium-ring-soft': transparentColor(theme.canvas.routeEntry, .16),
      }
      this.apply()
    },
    apply() {
      if (typeof document === 'undefined') return
      const root = document.documentElement
      root.classList.toggle('vf-theme-dark', this.name === 'dark')
      root.classList.toggle('vf-theme-light', this.name === 'light')
      root.dataset.vfTheme = this.name
      for (const [key, value] of Object.entries(this.skinVariables)) root.style.setProperty(key, value)
      for (const [key, value] of Object.entries(semanticAliases)) root.style.setProperty(key, value)
      root.style.colorScheme = this.name
      const styles = getComputedStyle(root)
      document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', styles.getPropertyValue('--bg-color').trim())
      try {
        sessionStorage.setItem('venue-audience-glass-theme-v1', JSON.stringify({
          name: this.name,
          page: styles.getPropertyValue('--bg-color').trim(),
          glass: styles.getPropertyValue('--glass-bg-strong').trim(),
          text: styles.getPropertyValue('--text-primary').trim(),
          accent: styles.getPropertyValue('--accent-primary').trim(),
          border: styles.getPropertyValue('--glass-border').trim(),
          highlight: styles.getPropertyValue('--glass-highlight').trim(),
          ring: styles.getPropertyValue('--stadium-ring').trim(),
          shadow: styles.getPropertyValue('--glass-shadow-soft').trim(),
          blur: styles.getPropertyValue('--blur-amount').trim(),
        }))
      } catch { /* VR can keep its fallback. */ }
    },
  },
})

function transparentColor(value: string, alpha: number): string {
  const match = value.match(/^#([a-f0-9]{6})$/i)
  if (!match) return value
  const hex = match[1] || ''
  const channels = [0, 2, 4].map((start) => Number.parseInt(hex.slice(start, start + 2), 16))
  return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${alpha})`
}

function isDarkColor(value: string): boolean {
  const match = value.match(/^#([a-f0-9]{6})$/i)
  if (!match) return true
  const hex = match[1] || ''
  const [r, g, b] = [0, 2, 4].map((start) => Number.parseInt(hex.slice(start, start + 2), 16))
  return ((r || 0) * 299 + (g || 0) * 587 + (b || 0) * 114) / 1000 < 150
}

function contrastText(value: string): string {
  return isDarkColor(value) ? '#F7FCFF' : '#04131F'
}
